import { useConnectProject } from '@trimble/connect-project-providers';
import { createResponseErrorLogger } from '@trimble/logging';
import { useLogger } from '@trimble/solidjs-utils';
import { useAuth } from '@trimble/trimble-id-solidjs';
import { createMemo, JSX } from 'solid-js';

import { FxWorkspaceContextProvider } from './FxWorkspaceContextProvider';
import { createFxWorkspaceRepository } from './fx-workspace-repository.factory';

export function FxWorkspaceRepositoryProvider(
  props: Readonly<{
    children: JSX.Element;
    fxWorkspaceDomain?: string;
    clientName: string;
    clientVersion: string;
  }>
): JSX.Element {
  const auth = useAuth();
  const { selectedTenantTrn } = useConnectProject();
  const logger = useLogger().getLogger('FxWorkspace');
  const apiErrorLogger = createResponseErrorLogger(logger);

  const repository = createMemo(() =>
    createFxWorkspaceRepository({
      fxWorkspaceDomain: props.fxWorkspaceDomain,
      clientName: props.clientName,
      clientVersion: props.clientVersion,
      getAccessToken: () => auth().getAccessTokenSilently(),
      getTenantTrn: () => selectedTenantTrn(),
      onResponseError: apiErrorLogger,
    })
  );

  // An empty key keeps the workspace queries idle until the tenant resolves.
  const repositoryKey = createMemo(() => {
    const domain = props.fxWorkspaceDomain?.trim();
    if (domain === undefined || domain === '') {
      return '';
    }

    const tenantTrn = selectedTenantTrn();
    if (tenantTrn === undefined) {
      return '';
    }

    return `${tenantTrn}|${domain}|${props.clientName}|${props.clientVersion}`;
  });

  return (
    <FxWorkspaceContextProvider
      repository={repository}
      repositoryKey={repositoryKey}
    >
      {props.children}
    </FxWorkspaceContextProvider>
  );
}
